import { io, Socket } from 'socket.io-client';
import { getServerUrl } from '../config';
import { fetchLgDevices, LgDevice } from './lgApi';
import { eventBus } from './EventBus';
import { patchRoomCardLive } from '../utils/roomCardIdentity';
import { createRoomMembership } from '../utils/roomMembership';

export const mapaLg: Record<number, LgDevice> = {};
let loaded = false;
let socket: Socket | null = null;
/** Un solo GET a la vez: un refresco vacío o atrasado no pisa al que ya mostró las tarjetas. */
let refreshInFlight: Promise<LgDevice[]> | null = null;

export function isMapaLgCargado(): boolean {
  return loaded;
}

const membership = createRoomMembership<LgDevice>();

export function getLgByHabitacion(habitacionId: number): LgDevice[] {
  return membership.cardsInRoom(habitacionId, mapaLg);
}

export function ensureLgInRoom(
  roomId: number,
  device: Partial<LgDevice> & { id: number },
): void {
  const id = Number(device.id);
  if (!membership.ensureInRoom(roomId, { ...device, id })) return;
  const locked = membership.cardsInRoom(roomId, mapaLg).find((d) => Number(d.id) === id);
  const prev = mapaLg[id];
  if (locked) mapaLg[id] = { ...(prev || locked), ...locked, id };
  eventBus.emit('lg:updated');
}

export function unlockLgRoom(id: number): void {
  membership.unlock(id);
}

function mergeList(devices: LgDevice[]) {
  let changed = false;
  devices.forEach((d) => {
    if (!d?.id) return;
    const id = Number(d.id);
    mapaLg[id] = { ...(mapaLg[id] || {}), ...d, id };
    membership.lockFromList(mapaLg[id]);
    changed = true;
  });
  if (changed) eventBus.emit('lg:updated');
}

function mergeLive(devices: Array<Partial<LgDevice> & { id?: number }>) {
  let changed = false;
  devices.forEach((d) => {
    const id = Number(d?.id);
    if (!Number.isFinite(id)) return;
    const next = patchRoomCardLive(mapaLg[id], { ...d, id });
    if (!next) return;
    mapaLg[id] = next;
    changed = true;
  });
  if (changed) eventBus.emit('lg:updated');
}

export function upsertLgDevice(device: LgDevice): void {
  if (!device?.id) return;
  mergeLive([device]);
}

/** Actualiza el mapa local (respuesta de control / refresh / PATCH). */
export function patchLgDevice(device: LgDevice): void {
  upsertLgDevice(device);
}

async function refrescarLgNow(): Promise<LgDevice[]> {
  try {
    const list = await fetchLgDevices();
    if (!Array.isArray(list)) return Object.values(mapaLg);
    if (list.length === 0 && Object.keys(mapaLg).length > 0) {
      loaded = true;
      return Object.values(mapaLg);
    }
    mergeList(list);
    loaded = true;
    return Object.values(mapaLg);
  } catch {
    loaded = true;
    return Object.values(mapaLg);
  }
}

export function refrescarLg(): Promise<LgDevice[]> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refrescarLgNow().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function cargarLg(): Promise<LgDevice[]> {
  ensureSocket();
  return refrescarLg();
}

function ensureSocket() {
  if (socket) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  let serverUrl = getServerUrl();
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    serverUrl = serverUrl.replace(/^http:/, 'https:').replace(/^ws:/, 'wss:');
  }
  socket = io(serverUrl, {
    path: '/socket.io-lg',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
  });
  socket.on('lg_status', (data: Partial<LgDevice> & { id?: number }) => {
    if (!data?.id) return;
    const id = Number(data.id);
    const existing = mapaLg[id];
    if (!existing) {
      void refrescarLg();
      return;
    }
    mergeLive([{ ...data, id }]);
  });
}
